import React, { useEffect, useRef, useState } from 'react';
import { Upload, Trash2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTenant } from '../../contexts/TenantContext';
import { getApiUrl } from '../../utils/api';

// Source d'un objet : bibliothèque 360 Resto (/brand/home) ou objet perso téléversé (custom:<id> → object storage)
export const objSrc = (id) => (id && id.startsWith('custom:') ? `${getApiUrl()}/nav-objects/${id.slice(7)}.png` : `/brand/home/${id}.webp`);
export const CUSTOM_EVENT = 'nav-custom-objects';

export const useCustomObjects = () => {
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${getApiUrl()}/nav-objects`, { withCredentials: true }).then((r) => setItems([...new Map((r.data.items || []).map((c) => [c.obj_id, c])).values()])).catch(() => {});
  useEffect(() => { load(); window.addEventListener(CUSTOM_EVENT, load); return () => window.removeEventListener(CUSTOM_EVENT, load); }, []);
  return items;
};

// Bibliothèque complète des objets découpés (DA 360 Resto) — partagée hero + sidebar
export const OBJECTS = [
  ['obj_sac_glovo', 'Sac Glovo'], ['obj_veste_jean', 'Veste en jean'], ['obj_montre_gshock', 'Montre G-Shock'], ['obj_baskets', 'Baskets'], ['obj_casque_orange', 'Casque orange'], ['obj_telephone_jaune', 'Téléphone à clapet'],
  ['obj_sac_rose', 'Sac rose'], ['obj_appareil_compact', 'Appareil compact'], ['obj_cafe_glace', 'Café glacé'], ['obj_lunettes_vertes', 'Lunettes vertes'], ['obj_polaroids', 'Polaroids'], ['obj_gloss_rose', 'Gloss rose'], ['obj_porte_cles', 'Porte-clés'],
  ['obj_pizza', 'Part de pizza'], ['obj_matcha', 'Matcha glacé'], ['obj_pasteque', 'Pastèque'], ['obj_cerises', 'Cerises'], ['obj_banane', 'Banane'], ['obj_croissant_dore', 'Croissant doré'],
  ['obj_cocktail_orange', 'Cocktail orange'], ['obj_fraise_croquee', 'Fraise croquée'], ['obj_chocolat', 'Chocolat'], ['obj_lunettes_jaunes', 'Lunettes jaunes'], ['obj_appareil_photo', 'Appareil photo argentique'], ['obj_avocat_coupe', 'Avocat coupé'],
  ['orange_slice', 'Orange coupée'], ['orange_whole', 'Orange'], ['sticker_citron', 'Citron'], ['sticker_pamplemousse', 'Pamplemousse'],
  ['sticker_fraise', 'Fraise'], ['sticker_tomate', 'Tomate'], ['sticker_figue', 'Figue'], ['sticker_poire', 'Poire'], ['nav_fiches', 'Avocat'],
  ['feuille_basilic', 'Basilic'], ['sticker_herbes', 'Herbes'], ['cocktail_spritz', 'Spritz'], ['pink_drink', 'Cocktail rosé'], ['nav_accueil', 'Croissant'], ['tiramisu', 'Tiramisu'],
  ['sticker_lunettes', 'Lunettes'], ['sticker_torchon', 'Torchon'], ['sticker_cuillere', 'Cuillère'], ['plateau_inox', 'Plateau'], ['toque_chef', 'Toque'], ['nav_social', 'Appareil photo'],
  ['nav_equipe', 'Toques & torchons'], ['nav_planning', 'Calendrier'], ['nav_pointage', 'Montre'], ['nav_taches', 'Bloc-notes'], ['nav_basecamp', 'Boussole'], ['nav_recrutement', 'Badge'],
  ['nav_rh', 'Dossier kraft'], ['nav_conges', 'Chapeau de paille'], ['nav_documents', 'Enveloppe'], ['nav_juridique', 'Stylo plume'], ['nav_stocks', 'Cageot de fruits'], ['nav_dlc', 'Minuteur'],
  ['nav_haccp', 'Thermomètre'], ['nav_fournisseurs', 'Cageot de légumes'], ['nav_achats_recurrents', 'Sac de courses'], ['nav_commandes', 'Facture'], ['nav_tickets', 'Ticket'], ['nav_materiel', 'Carton'],
  ['nav_finance', 'Billets & pièces'], ['nav_caisse', 'Tiroir-caisse'], ['nav_facturation', 'Facture roulée'], ['nav_livraisons', 'Sac de livraison'], ['nav_caisse_connect', 'Terminal CB'],
  ['nav_carte_membre', 'Carte membre'], ['nav_abonnement', 'Carte dorée'], ['nav_cle', 'Clé'], ['nav_acces', 'Cadenas'], ['nav_mise_en_route', 'Boîte à outils'], ['nav_settings', 'Outils'],
  ['nav_integrations', 'Prise'], ['nav_studio', 'Pinceau'], ['nav_chat', 'Téléphone'], ['nav_meteo', 'Parapluie'], ['nav_aide', 'Bouée'],
];

export const ObjectGrid = ({ value, onPick, disabled, cols = 6, testPrefix = 'obj' }) => {
  const custom = useCustomObjects();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append('file', f);
    try { const r = await axios.post(`${getApiUrl()}/nav-objects`, fd, { withCredentials: true }); window.dispatchEvent(new Event(CUSTOM_EVENT)); toast.success(`« ${r.data.name} » ajouté à la bibliothèque`); onPick(`custom:${r.data.obj_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Import impossible'); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const remove = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    try { await axios.delete(`${getApiUrl()}/nav-objects/${id}`, { withCredentials: true }); window.dispatchEvent(new Event(CUSTOM_EVENT)); toast.success('Objet supprimé'); } catch { toast.error('Suppression impossible'); }
  };
  return (
    <div className="da-obj-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }} data-testid={`${testPrefix}-grid`}>
      <button type="button" className="da-obj-upload" disabled={disabled || busy} title="Importer un PNG détouré (fond transparent, 4 Mo max)" onClick={() => fileRef.current?.click()} data-testid={`${testPrefix}-upload`}>
        {busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}<small>Mon PNG</small>
        <input ref={fileRef} type="file" accept="image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testPrefix}-upload-input`} />
      </button>
      {custom.map((c) => {
        const id = `custom:${c.obj_id}`;
        return (
          <button type="button" key={id} className={`da-obj-custom ${value === id ? 'is-on' : ''}`} disabled={disabled} title={c.name} onClick={() => onPick(id)} data-testid={`${testPrefix}-${c.obj_id}`}>
            <img src={objSrc(id)} alt={c.name} loading="lazy" />
            <span role="button" className="da-obj-del" title="Supprimer cet objet" onClick={(e) => remove(e, c.obj_id)} data-testid={`${testPrefix}-del-${c.obj_id}`}><Trash2 size={11} /></span>
          </button>
        );
      })}
      {OBJECTS.map(([id, label]) => (
        <button type="button" key={id} className={value === id ? 'is-on' : ''} disabled={disabled} title={label} onClick={() => onPick(id)} data-testid={`${testPrefix}-${id}`}>
          <img src={objSrc(id)} alt={label} loading="lazy" />
        </button>
      ))}
    </div>
  );
};

export const useBrandingSave = () => {
  const { setTenant } = useTenant();
  return async (key, next, okMsg) => {
    try {
      await axios.put(`${getApiUrl()}/tenant/settings`, { [key]: next }, { withCredentials: true });
      setTenant((t) => ({ ...t, branding: { ...(t?.branding || {}), [key]: next } }));
      toast.success(okMsg);
      return true;
    } catch { toast.error('Impossible d’enregistrer'); return false; }
  };
};
